import type { MouseEvent } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';

import { toast } from 'sonner';
import { usePopover } from 'minimal-shared/hooks';
import { useMemo, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesUpDown } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck as fasCircleCheck } from '@fortawesome/pro-solid-svg-icons';

import Box from '@mui/material/Box';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { useTranslate } from 'src/locales';

import { Scrollbar } from 'src/components/scrollbar';
import { CustomPopover } from 'src/components/custom-popover';

import { useAuthContext, useAvailableAdminRoles } from 'src/auth/hooks';

export type AdminRolesSwitcherProps = ButtonBaseProps & {
  sx?: SxProps<Theme>;
};

export function AdminRolesSwitcher({ sx, ...other }: AdminRolesSwitcherProps) {
  const mediaQuery = 'sm';

  const { open, anchorEl, onClose, onOpen } = usePopover();

  const { adminRoles, adminRolesLoading } = useAvailableAdminRoles();
  const { assumeRole, isSwitchingRole, assumedRoleId } = useAuthContext();
  const { t } = useTranslate('common');

  const [optimisticRoleId, setOptimisticRoleId] = useState<number | null>(null);

  const currentRole = adminRoles.find((role) => role.id === assumedRoleId);

  const displayedRole = useMemo(() => {
    if (isSwitchingRole && optimisticRoleId) {
      return adminRoles.find((role) => role.id === optimisticRoleId);
    }
    return currentRole;
  }, [isSwitchingRole, optimisticRoleId, adminRoles, currentRole]);

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (isSwitchingRole) return;
    onOpen(event);
  };

  const handleCloseMenu = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSelectRole = useCallback(
    async (roleId: number) => {
      if (roleId === currentRole?.id || isSwitchingRole) {
        handleCloseMenu();
        return;
      }

      const selectedRole = adminRoles.find((role) => role.id === roleId);

      handleCloseMenu();

      if (!assumeRole || !selectedRole) {
        return;
      }

      setOptimisticRoleId(roleId);

      try {
        await assumeRole(roleId);
        setOptimisticRoleId(null);
        toast.success(t('roleSwitcher.switchSuccess', { roleName: selectedRole.displayName }));
      } catch {
        setOptimisticRoleId(null);
        toast.error(t('roleSwitcher.switchError'));
      }
    },
    [currentRole, isSwitchingRole, adminRoles, assumeRole, handleCloseMenu, t]
  );

  const buttonBg: SxProps<Theme> = {
    height: 1,
    zIndex: -1,
    opacity: 0,
    content: "''",
    borderRadius: 1,
    position: 'absolute',
    visibility: 'hidden',
    bgcolor: 'action.hover',
    width: 'calc(100% + 8px)',
    transition: (theme) =>
      theme.transitions.create(['opacity', 'visibility'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    ...(open && {
      opacity: 1,
      visibility: 'visible',
    }),
  };

  const renderButton = () => (
    <ButtonBase
      disableRipple
      onClick={handleButtonClick}
      disabled={adminRolesLoading || adminRoles.length === 0 || isSwitchingRole}
      sx={[
        {
          py: 0.5,
          gap: { xs: 0.5, [mediaQuery]: 1 },
          '&::before': buttonBg,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Box
        component="span"
        sx={{
          typography: 'subtitle2',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          py: 1,
        }}
      >
        {adminRolesLoading
          ? t('roleSwitcher.loading')
          : displayedRole?.displayName || t('roleSwitcher.selectRole')}
      </Box>
      {isSwitchingRole ? (
        <CircularProgress size={16} sx={{ color: 'inherit' }} />
      ) : (
        <FontAwesomeIcon icon={faAnglesUpDown} style={{ color: 'var(--palette-text-primary)' }} />
      )}
    </ButtonBase>
  );

  const renderMenuList = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={handleCloseMenu}
      slotProps={{
        arrow: { placement: 'top-left' },
        paper: { sx: { mt: 0.5, ml: -0.5, width: 320 } },
      }}
    >
      <Scrollbar sx={{ maxHeight: 320 }}>
        <MenuList>
          {adminRoles.map((role) => (
            <MenuItem
              key={role.id}
              selected={role.id === assumedRoleId}
              onClick={() => handleSelectRole(role.id)}
              sx={{ height: 48 }}
            >
              <Typography
                noWrap
                component="span"
                variant="body2"
                sx={{ flexGrow: 1, fontWeight: 'fontWeightMedium' }}
              >
                {role.displayName}
              </Typography>

              {role.id === assumedRoleId && (
                <FontAwesomeIcon
                  icon={fasCircleCheck}
                  size="lg"
                  style={{ color: 'var(--palette-success-main)' }}
                />
              )}
            </MenuItem>
          ))}
        </MenuList>
      </Scrollbar>
    </CustomPopover>
  );

  return (
    <>
      {renderButton()}
      {renderMenuList()}
    </>
  );
}
